import React, { useEffect } from "react";
import { ColorModeContext, useMode } from "./theme";
import {
  Box,
  CircularProgress,
  CssBaseline,
  ThemeProvider
} from "@mui/material";
import { Routes, Route, useNavigate } from "react-router-dom";
import Dashboard from "./scenes/dashboard";
import Sidebar from "./scenes/global/Sidebar";
import AddTest from "./scenes/add";
import { StyledEngineProvider } from "@mui/material";
import { Auth0Provider } from "@auth0/auth0-react";
import VideoProvider from "./context/video_context";
import { useLocation } from "react-router-dom";
import APIProvider from "./context/api_hook";
import useAuth from "./hooks/auth_hook";
import usePayment from "./hooks/payment_hook";
import { ToastContainer } from "react-toastify";
import Tests from "./scenes/tests";
import Test from "./scenes/test";
import TestProvider from "./context/test_context";
import Settings from "./scenes/settings";
import ViewProvider from "./context/view_context";
import CurrentTests from "./scenes/current";
import FinishedTests from "./scenes/finished";
import MetricsProvider from "./context/metrics_context";
import Checkout from "./scenes/checkout";

// Define a type for route objects
interface RouteConfig {
  path: string;
  element: React.ReactNode;
  index?: boolean;
}

const IS_DEBUG = process.env.REACT_APP_DEBUG === "true";


function LogOut() {
  const { logout } = useAuth();
  console.log("LOGGING OUT");
  logout();
  return <Box></Box>;
}

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPaid, isCheckingPayment } = usePayment();

  useEffect(() => {
    // Don't do anything until the payment check is complete
    if (isCheckingPayment) {
      return;
    }

    if (location.pathname === "/") {
      if (hasPaid) {
        // If the user has paid, send them to the main app
        navigate("/add");
      } else {
        // If the user has NOT paid, send them directly to checkout
        navigate("/checkout");
      }
    }
  }, [location, navigate, hasPaid, isCheckingPayment]);

  // Show a spinner while we determine where to send the user
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh"
      }}
    >
      <CircularProgress />
    </Box>
  );
};

// This new component contains the core application logic and layout.
// It assumes that all necessary providers (including theme) are available from its parents.
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();

  // Define routes that don't require payment
  const public_routes: RouteConfig[] = [
    { path: "/", element: <Home /> },
    { path: "/checkout", element: <Checkout /> },
    { path: "/logout", element: <LogOut /> }
  ];

  // Define routes that require payment
  const paid_routes: RouteConfig[] = [
    { path: "/add", element: <AddTest />, index: true },
    { path: "/settings", element: <Settings /> },
    { path: "/tests", element: <Tests /> },
    { path: "/current", element: <CurrentTests /> },
    { path: "/finished", element: <FinishedTests /> },
    { path: "/tests/:testid", element: <Test /> },
    { path: "/previous", element: <Dashboard /> },
    { path: "/metrics", element: <Dashboard /> }
  ];

  // Combine all routes
  const authenticated_routes: RouteConfig[] = [...paid_routes, ...public_routes];

  const { loginWithRedirect, isAuthenticated, isLoading, user } = useAuth();
  const { hasPaid, isCheckingPayment } = usePayment();

  const is_authenticated_route = authenticated_routes.some((route) => {
    return route.path === location.pathname;
  });
  // Redirect to login if not authenticated
  if (is_authenticated_route && !isAuthenticated && !isLoading && !IS_DEBUG) {
    loginWithRedirect();
  }

  const is_paid_route = paid_routes.some((route) => {
    return route.path === location.pathname;
  });

  // Effect to handle redirection after payment check is complete
  useEffect(() => {
    // When loading is finished, if we are on a paid route and the user hasn't paid, redirect.
    if (!isLoading && !isCheckingPayment && is_paid_route && !hasPaid) {
      navigate("/checkout");
    }
  }, [isLoading, isCheckingPayment, hasPaid, is_paid_route, navigate]);


  return (
    <div className="app">
      <main className={"content"}>
        {!isLoading && (user || IS_DEBUG) && !(is_paid_route && isCheckingPayment) ? (
          <TestProvider>
            <VideoProvider>
              <MetricsProvider>
                <ViewProvider>
                  <Sidebar user={user} />
                  <Routes>
                    {authenticated_routes.map((route) => {
                      return (
                        <Route
                          key={route.path}
                          path={route.path}
                          element={route.element}
                          index={route.index}
                        />
                      );
                    })}
                  </Routes>
                </ViewProvider>
              </MetricsProvider>
            </VideoProvider>
          </TestProvider>
        ) : (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh"
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </main>
    </div>
  );
}


function App() {
  const [theme, colorMode] = useMode();

  return (
    <Auth0Provider
      domain="auth.forcreators.xyz"
      clientId="ODlnTxjgjCY92KsE045tUeS4klb9k2MZ"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: "https://dev-6paf5mmzujgbxltb.us.auth0.com/api/v2/",
        scope: "read:current_user update:current_user_metadata"
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <APIProvider>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <StyledEngineProvider injectFirst>
              <CssBaseline />
              <AppContent />
              <ToastContainer position={"bottom-right"} />
            </StyledEngineProvider>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </APIProvider>
    </Auth0Provider>
  );
}

export default App;
